import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { catalogs, it, type TranslationCatalog } from './catalogs';

/**
 * Contesto di localizzazione (§21).
 *
 * La lingua scelta è esposta a tutta l'app e riportata sull'elemento `<html>`,
 * perché screen reader e correttori la usino davvero.
 */

type I18nValue = {
  readonly language: string;
  readonly available: readonly string[];
  readonly t: TranslationCatalog;
  readonly setLanguage: (language: string) => void;
};

const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = 'flc.language';

/**
 * Preferenza di lingua dell'operatore.
 *
 * È una comodità di interfaccia e non contiene dati personali, quindi può
 * stare in `localStorage`. Nulla di ciò che riguarda sessione o visitatori vi
 * viene mai scritto (§10.1).
 */
function readStoredLanguage(available: readonly string[]): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && available.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

function detectLanguage(available: readonly string[], fallback: string): string {
  const stored = readStoredLanguage(available);
  if (stored) return stored;

  for (const candidate of navigator.languages ?? []) {
    if (available.includes(candidate)) return candidate;
    const base = candidate.split('-')[0];
    if (base && available.includes(base)) return base;
  }
  return fallback;
}

export function I18nProvider({
  children,
  available = Object.keys(catalogs),
  fallback = 'it',
}: {
  children: ReactNode;
  available?: readonly string[];
  fallback?: string;
}) {
  const [language, setLanguageState] = useState(() => detectLanguage(available, fallback));

  const setLanguage = useCallback(
    (next: string) => {
      if (!available.includes(next)) return;
      setLanguageState(next);
      document.documentElement.lang = next;
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Modalità privata o storage disabilitato: la lingua resta valida per
        // questa sessione, non è un errore da mostrare.
      }
    },
    [available],
  );

  const value = useMemo<I18nValue>(
    () => ({
      language,
      available,
      // Il fallback al catalogo italiano riguarda la sola UI. I testi di
      // consenso non hanno fallback: vedi `assertConsentLanguageAvailable`.
      t: catalogs[language] ?? it,
      setLanguage,
    }),
    [language, available, setLanguage],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n richiede I18nProvider.');
  return context;
}

/** Scorciatoia per il solo catalogo. */
export function useTranslation(): TranslationCatalog {
  return useI18n().t;
}
